// libraries
import type { CreditDecision, FldLenderDecision, StateTree } from '@makemydeal/dr-dash-types';
import { DealerDecisionStatus } from '@makemydeal/dr-dash-types';
import { getCurrentOfferType } from './offerRedux';

const DECISION_STATUSES = [
    DealerDecisionStatus.APPROVED,
    DealerDecisionStatus.CONDITIONALLY_APPROVED,
    DealerDecisionStatus.DECLINED
];

export const getFullLenderDecisionsList = (state: StateTree): FldLenderDecision[] => {
    return state.creditDecision?.fullLenderDecisions?.lenderDecisions || [];
};

export const getFullLenderDecisionsAsCreditDecisionList = (state: StateTree): CreditDecision[] => {
    const lenderDecisions = getFullLenderDecisionsList(state);
    const financeMethod = state.creditDecision?.fullLenderDecisions?.financeMethod || '';
    return lenderDecisions.map((decision) => ({
        lenderName: decision.lenderName || '',
        lenderId: decision.lenderId || '',
        approvedAmount: decision.approvedAmount ?? 0,
        approvedTerm: decision.approvedTerm ?? 0,
        approvedRate: decision.approvedRate ?? decision.customerRate ?? 0,
        approvalStatus: decision.status || '',
        financeMethod: decision.financeMethod || financeMethod,
        monthlyPayment: decision.monthlyPayment ?? 0,
        lenderLogo: '',
        buyRate: decision.buyRate ?? 0
    }));
};

export const getHasFullLenderDecisions = (state: StateTree): boolean => {
    const lenderDecisions = getFullLenderDecisionsList(state);
    return lenderDecisions.length > 0;
};

const getFullLenderDecisionsByStatus = (state: StateTree, status: DealerDecisionStatus): FldLenderDecision[] => {
    const lenderDecisions = getFullLenderDecisionsList(state);
    return lenderDecisions.filter((decision) => decision.status === status);
};

export const getApprovedFullLenderDecisions = (state: StateTree): FldLenderDecision[] => {
    return getFullLenderDecisionsByStatus(state, DealerDecisionStatus.APPROVED);
};

export const getCounterOfferFullLenderDecisions = (state: StateTree): FldLenderDecision[] => {
    return getFullLenderDecisionsByStatus(state, DealerDecisionStatus.CONDITIONALLY_APPROVED);
};

export const getDeclinedFullLenderDecisions = (state: StateTree): FldLenderDecision[] => {
    return getFullLenderDecisionsByStatus(state, DealerDecisionStatus.DECLINED);
};

export const getOtherFullLenderDecisions = (state: StateTree): FldLenderDecision[] => {
    const lenderDecisions = getFullLenderDecisionsList(state);
    return lenderDecisions.filter((decision) => !DECISION_STATUSES.includes(decision.status as DealerDecisionStatus));
};

export const getIsLoadingFullLenderDecisions = (state: StateTree): boolean => {
    return state.creditDecision?.fullLenderDecisions?.isLoadingLenderDecisions || false;
};

export const getFullLenderDecisionsFinanceMethod = (state: StateTree): string => {
    // R1 responses return blank ("") top-level financeMethod but populate it per-decision
    const fld = state.creditDecision?.fullLenderDecisions;
    return fld?.financeMethod || fld?.lenderDecisions?.[0]?.financeMethod || '';
};

export const isFldFinanceMethodSameAsCurrentOfferType = (state: StateTree): boolean => {
    const fldFinanceMethod = getFullLenderDecisionsFinanceMethod(state);
    const currentDealFinanceMethod = getCurrentOfferType(state);
    return fldFinanceMethod.trim().toLowerCase() === currentDealFinanceMethod.trim().toLowerCase();
};
